import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import AddIcon from '@mui/icons-material/Add';
import ListAltOutlinedIcon from '@mui/icons-material/ListAltOutlined';
import { LockOutlined as LockOutlinedIcon } from '@mui/icons-material';
import { useProductosMaterialContext } from 'context';
import { useApi } from 'lib/api';
import {
  PROGRAMA_NOVEDADES_NOMINA,
  puedeVerProgramaNominaPropio,
} from 'lib/seguridad/permisosPorModulo';
import { FP, PageHeader, fpInputSx } from 'components/financepro';
import GenerarNomina from './GenerarNomina';

export { PROGRAMA_NOVEDADES_NOMINA };

const btnBase = { textTransform: 'none', borderRadius: 1.8, fontWeight: 700, boxShadow: 'none' };
const btnOutline = (c) => ({
  ...btnBase,
  color: c,
  bgcolor: '#FFFFFF',
  border: `1px solid ${c}`,
  '&:hover': { bgcolor: `${c}12`, border: `1px solid ${c}`, color: c },
  '&.Mui-disabled': { color: FP.color.textMuted, borderColor: FP.color.border },
});
const btnSolid = (bg, hover) => ({
  ...btnBase,
  bgcolor: bg,
  color: '#FFFFFF',
  '&:hover': { bgcolor: hover, boxShadow: 'none', color: '#FFFFFF' },
  '&.Mui-disabled': { bgcolor: `${bg}66`, color: '#FFFFFF' },
});

const cardShellSx = {
  borderRadius: 2.5,
  bgcolor: FP.color.cardBg,
  boxShadow: FP.shadow.soft,
  border: `1px solid ${FP.color.border}`,
  overflow: 'hidden',
};

const sectionBox = {
  border: `1px solid ${FP.color.border}`,
  borderRadius: 2.5,
  p: { xs: 2, sm: 2.6 },
  mb: 0,
  bgcolor: FP.color.cardBg,
  boxShadow: FP.shadow.soft,
};

const tabSx = {
  minHeight: 40,
  bgcolor: 'transparent',
  p: 0,
  borderRadius: 0,
  '& .MuiTab-root': {
    textTransform: 'none',
    fontWeight: 700,
    minHeight: 40,
    color: FP.color.textSecondary,
    opacity: 1,
  },
  '& .MuiTab-root.Mui-selected': { color: FP.color.navy },
  '& .MuiTabs-indicator': {
    backgroundColor: FP.color.navy,
    height: 3,
    borderRadius: '3px 3px 0 0',
    boxShadow: 'none',
  },
};

function formularioVacio() {
  return {
    numemp: null,
    nombres: '',
    feriado: '',
    ajuste: '',
    incentivo: '',
    otros_ingresos: '',
    comision_ventas: '',
    avance_regalia: '',
    salario_normal: '',
    salario_extra: '',
    horas_extras_35: '',
    horas_extras_100: '',
    isr: '',
    ars: '',
    afp: '',
    prestamo: '',
    otros_descuentos: '',
  };
}

function fmt(valor) {
  const n = Number(valor);
  if (!n) return '';
  return n.toFixed(2);
}

const NovedadesNomina = () => {
  const { controller, selectedCompany, showSuccess, showError } = useProductosMaterialContext();
  const { usuarioLog, roleDetalles, nombreNegocio } = controller;
  const { post } = useApi();

  const puedeLeer = useMemo(
    () => puedeVerProgramaNominaPropio(roleDetalles, PROGRAMA_NOVEDADES_NOMINA),
    [roleDetalles]
  );

  const [empleados, setEmpleados] = useState([]);
  const [form, setForm] = useState(() => formularioVacio());
  const [pestana, setPestana] = useState(0);
  const [cargando, setCargando] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [empleadoCargado, setEmpleadoCargado] = useState(false);
  const [vistaGenerarNomina, setVistaGenerarNomina] = useState(false);

  const puedeOperar = Boolean(selectedCompany?.db_empresa && usuarioLog);

  const credencialesEmpresa = useCallback(
    () => ({ id_empresa: selectedCompany }),
    [selectedCompany]
  );

  const cargarEmpleados = useCallback(async () => {
    if (!puedeLeer || !puedeOperar) return;
    try {
      const { ok, data } = await post('/api/nomina_listarempleados', credencialesEmpresa());
      if (ok && Array.isArray(data.empleados)) {
        setEmpleados(data.empleados);
      }
    } catch {
      /* silencioso – solo carga el catálogo */
    }
  }, [puedeLeer, puedeOperar, post, credencialesEmpresa]);

  useEffect(() => {
    if (puedeLeer && puedeOperar) cargarEmpleados();
  }, [puedeLeer, puedeOperar, cargarEmpleados]);

  const buscarEmpleado = useCallback(
    async (numemp) => {
      if (!numemp) return;
      setCargando(true);
      try {
        const { ok, data } = await post('/api/nomina_obtenernovedades', {
          ...credencialesEmpresa(),
          numemp: String(typeof numemp === 'object' ? numemp.numemp : numemp),
        });
        if (!ok) throw new Error(data.Error || 'Error al consultar novedades');
        const n = data.novedades || {};
        if (Array.isArray(data.empleados) && data.empleados.length) {
          setEmpleados(data.empleados);
        }
        setForm({
          numemp:
            typeof numemp === 'object'
              ? numemp
              : empleados.find((e) => String(e.numemp) === String(numemp)) || null,
          nombres: n.nombres || '',
          feriado: fmt(n.feriado),
          ajuste: fmt(n.ajuste),
          incentivo: fmt(n.incentivo),
          otros_ingresos: fmt(n.otros_ingresos),
          comision_ventas: fmt(n.comision_ventas),
          avance_regalia: fmt(n.avance_regalia),
          salario_normal: fmt(n.salario_normal),
          salario_extra: fmt(n.salario_extra),
          horas_extras_35: fmt(n.horas_extras_35),
          horas_extras_100: fmt(n.horas_extras_100),
          isr: fmt(n.isr),
          ars: fmt(n.ars),
          afp: fmt(n.afp),
          prestamo: fmt(n.prestamo),
          otros_descuentos: fmt(n.otros_descuentos),
        });
        setEmpleadoCargado(true);
        setPestana(0);
      } catch (e) {
        showError(e.message || 'Error al consultar novedades');
      } finally {
        setCargando(false);
      }
    },
    [post, credencialesEmpresa, empleados, showError]
  );

  const limpiar = () => {
    setForm(formularioVacio());
    setEmpleadoCargado(false);
    setPestana(0);
  };

  const setCampoDecimal = (campo) => (evento) => {
    const raw = evento?.target?.value ?? '';
    const limpio = raw.replace(/[^0-9.]/g, '');
    setForm((prev) => ({ ...prev, [campo]: limpio }));
  };

  const guardar = async () => {
    if (!form.numemp) {
      showError('No ha seleccionado ningún empleado');
      return;
    }
    setGuardando(true);
    try {
      const numemp = typeof form.numemp === 'object' ? form.numemp.numemp : form.numemp;
      const { ok, data } = await post('/api/nomina_guardarnovedades', {
        ...credencialesEmpresa(),
        numemp: String(numemp),
        ajuste: form.ajuste || '0',
        incentivo: form.incentivo || '0',
        otros_ingresos: form.otros_ingresos || '0',
        comision_ventas: form.comision_ventas || '0',
        avance_regalia: form.avance_regalia || '0',
        horas_extras_35: form.horas_extras_35 || '0',
        horas_extras_100: form.horas_extras_100 || '0',
        otros_descuentos: form.otros_descuentos || '0',
      });
      if (!ok) throw new Error(data.Error || 'No se pueden grabar los datos');
      showSuccess(data.ok || 'Novedades guardadas');
      limpiar();
    } catch (e) {
      showError(e.message || 'No se pueden grabar los datos');
    } finally {
      setGuardando(false);
    }
  };

  const renderAcceso = (mensaje) => (
    <Box component="main" sx={{ minHeight: '100vh', bgcolor: FP.color.surface }}>
      <PageHeader
        title="Novedades Nómina"
        subtitle={nombreNegocio ? `${nombreNegocio} · Nómina` : 'Nómina'}
      />
      <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 }, display: 'flex', justifyContent: 'center' }}>
        <Card
          elevation={0}
          sx={{ ...cardShellSx, maxWidth: 560, width: '100%', textAlign: 'center', p: { xs: 3, md: 4 } }}
        >
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: 1.6,
              mx: 'auto',
              mb: 2.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: FP.color.dangerSoft,
              color: FP.color.danger,
            }}
          >
            <LockOutlinedIcon sx={{ fontSize: 30 }} />
          </Box>
          <Typography
            sx={{
              fontWeight: 800,
              fontSize: '1.25rem',
              color: FP.color.textPrimary,
              mb: 1,
              letterSpacing: '-0.01em',
            }}
          >
            Acceso denegado
          </Typography>
          <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.95rem', mb: 1.2 }}>
            {mensaje}
          </Typography>
          <Typography sx={{ color: FP.color.textMuted, fontSize: '0.85rem' }}>
            Por favor, contacte al administrador del sistema para solicitar los permisos necesarios.
          </Typography>
        </Card>
      </Box>
    </Box>
  );

  if (!puedeLeer) {
    return renderAcceso('Su rol no tiene acceso a esta opción (novedadesnomina).');
  }
  if (!puedeOperar) {
    return renderAcceso('Seleccione una empresa para administrar las novedades de nómina.');
  }

  if (vistaGenerarNomina) {
    return <GenerarNomina onVolver={() => setVistaGenerarNomina(false)} />;
  }

  const campoSoloLectura = (label, valor) => (
    <TextField
      fullWidth
      label={label}
      value={valor}
      InputProps={{ readOnly: true }}
      InputLabelProps={{ shrink: true }}
      sx={{
        ...fpInputSx,
        '& .MuiInputBase-root': { bgcolor: '#F8F9FA' },
      }}
    />
  );

  const campoEditable = (campo, label) => (
    <TextField
      fullWidth
      label={label}
      value={form[campo]}
      onChange={setCampoDecimal(campo)}
      placeholder="0.00"
      inputProps={{ inputMode: 'decimal' }}
      InputLabelProps={{ shrink: true }}
      sx={fpInputSx}
    />
  );

  return (
    <Box component="main" sx={{ minHeight: '100vh', bgcolor: FP.color.surface }}>
      <PageHeader
        title="Novedades Nómina"
        subtitle={
          nombreNegocio
            ? `${nombreNegocio} · Ingresos y descuentos del período activo`
            : 'Ingresos y descuentos del período activo'
        }
        cta={
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button startIcon={<ListAltOutlinedIcon />} onClick={() => setVistaGenerarNomina(true)} sx={btnOutline(FP.color.navy)}>
              Nómina
            </Button>
            <Button startIcon={<AddIcon />} onClick={limpiar} sx={btnOutline(FP.color.navy)}>
              Nuevo
            </Button>
          </Box>
        }
      />
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        <Box sx={sectionBox}>
          {/* Selector de empleado */}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={4}>
              <Autocomplete
                options={empleados}
                value={form.numemp}
                onChange={(_, v) => {
                  if (v) {
                    buscarEmpleado(v);
                  } else {
                    limpiar();
                  }
                }}
                getOptionLabel={(o) => o?.label || ''}
                isOptionEqualToValue={(a, b) =>
                  String(a?.numemp) === String(b?.numemp)
                }
                disabled={empleadoCargado}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Empleado"
                    placeholder="Buscar empleado..."
                    InputLabelProps={{ shrink: true }}
                    sx={fpInputSx}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={8}>
              <TextField
                fullWidth
                label="Nombres"
                value={form.nombres}
                InputProps={{ readOnly: true }}
                InputLabelProps={{ shrink: true }}
                sx={{
                  ...fpInputSx,
                  '& .MuiInputBase-root': { bgcolor: '#F8F9FA' },
                }}
              />
            </Grid>
          </Grid>

          {cargando ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress size={36} />
            </Box>
          ) : empleadoCargado ? (
            <>
              <Tabs
                value={pestana}
                onChange={(_, v) => setPestana(v)}
                sx={{ ...tabSx, mb: 2 }}
              >
                <Tab label="Ingresos" />
                <Tab label="Descuentos" />
              </Tabs>

              <Grid container spacing={2}>
                {pestana === 0 && (
                  <>
                    <Grid item xs={12} sm={6}>
                      {campoSoloLectura('Feriado', form.feriado)}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      {campoEditable('ajuste', 'Ajuste')}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      {campoEditable('incentivo', 'Incentivo')}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      {campoEditable('otros_ingresos', 'Otros Ingresos')}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      {campoSoloLectura('Salario Normal', form.salario_normal)}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      {campoSoloLectura(
                        'Salario Extra',
                        fmt(
                          (Number(form.horas_extras_35) || 0) +
                            (Number(form.horas_extras_100) || 0)
                        )
                      )}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      {campoEditable('horas_extras_35', 'Horas Extras 35%')}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      {campoEditable('horas_extras_100', 'Horas Extras 100%')}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      {campoEditable('comision_ventas', 'Com. Ventas')}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      {campoEditable('avance_regalia', 'Av. Regalía')}
                    </Grid>
                  </>
                )}
                {pestana === 1 && (
                  <>
                    <Grid item xs={12} sm={6}>
                      {campoSoloLectura('ISR', form.isr)}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      {campoSoloLectura('ARS', form.ars)}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      {campoSoloLectura('AFP', form.afp)}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      {campoSoloLectura('Préstamo', form.prestamo)}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      {campoEditable('otros_descuentos', 'Otros Descuentos')}
                    </Grid>
                  </>
                )}
              </Grid>

              {/* Botón Guardar */}
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3, gap: 1.5 }}>
                <Button onClick={limpiar} disabled={guardando} sx={btnOutline(FP.color.textSecondary)}>
                  Cancelar
                </Button>
                <Button
                  onClick={guardar}
                  disabled={guardando}
                  startIcon={
                    guardando ? <CircularProgress size={16} color="inherit" /> : <SaveOutlinedIcon />
                  }
                  sx={btnSolid(FP.color.navy, FP.color.navyDeep)}
                >
                  {guardando ? 'Guardando…' : 'Guardar'}
                </Button>
              </Box>
            </>
          ) : (
            <Card elevation={0} sx={{ bgcolor: '#F8F9FA', borderRadius: 2, mt: 1 }}>
              <CardContent sx={{ textAlign: 'center', py: 5 }}>
                <Typography sx={{ fontWeight: 700, color: FP.color.textSecondary, fontSize: '0.95rem' }}>
                  Seleccione un empleado para ver y editar sus novedades
                </Typography>
                <Typography sx={{ color: FP.color.textMuted, fontSize: '0.85rem', mt: 0.5 }}>
                  Use el campo de búsqueda para encontrar al empleado por número o nombre.
                </Typography>
              </CardContent>
            </Card>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default NovedadesNomina;
